// DICOMViewer.jsx
// Full-featured DICOM viewer built on Cornerstone3D.
// Opens as a full-screen portal modal.
// Props: studyId (uuid), patientId, onClose

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
import { storage } from "../lib/storage";
import { initCornerstone } from "../utils/cornerstoneInit";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:    "#0a1525", navy2: "#0f1e2e", navy3: "#162436", navy4: "#1d2f40",
  teal:    "#0ea5a0", tealLt: "rgba(14,165,160,0.15)",
  amber:   "#f0a500", amberLt: "rgba(240,165,0,0.12)",
  text1:   "#e8edf2", text2: "#8fa3b4", text3: "#4a6070",
  border:  "rgba(255,255,255,0.07)", border2: "rgba(255,255,255,0.14)",
  toolbar: "#0d1b2a",
};

// ─── WINDOW PRESETS ───────────────────────────────────────────────────────────
const WINDOW_PRESETS = [
  { label: "Auto",        wc: null,  ww: null  },
  { label: "Bone",        wc: 400,   ww: 1500  },
  { label: "Soft Tissue", wc: 50,    ww: 350   },
  { label: "Lung",        wc: -600,  ww: 1500  },
  { label: "Brain",       wc: 40,    ww: 80    },
  { label: "Abdomen",     wc: 60,    ww: 400   },
];

// ─── MODALITY COLOR MAP ───────────────────────────────────────────────────────
const MODALITY_COLOR = {
  XR: "#3b82f6", CR: "#3b82f6", DX: "#3b82f6",
  CT: "#f0a500",
  MR: "#8b5cf6", MRI: "#8b5cf6",
  US: "#0ea5a0",
  MG: "#e05252",
  NM: "#22c55e", PT: "#22c55e",
};
const modalityColor = (m) => MODALITY_COLOR[m?.toUpperCase()] || "#8fa3b4";

// ─── TOOLBAR BUTTON ───────────────────────────────────────────────────────────
function TBtn({ label, title, active, onClick, disabled }) {
  return (
    <button
      title={title || label}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: active ? C.tealLt : "transparent",
        border: `1px solid ${active ? C.teal : C.border2}`,
        borderRadius: 5, color: active ? C.teal : C.text2,
        padding: "4px 9px", fontSize: 11, cursor: disabled ? "not-allowed" : "pointer",
        whiteSpace: "nowrap", opacity: disabled ? 0.4 : 1,
        transition: "all 150ms",
      }}
    >
      {label}
    </button>
  );
}

// ─── MAIN VIEWER ──────────────────────────────────────────────────────────────
export default function DICOMViewer({ studyId, patientId, onClose }) {
  const viewportRef   = useRef(null);
  const engineRef     = useRef(null);
  const viewportIdRef = useRef(`csViewport_${Date.now()}`);
  const renderEngineId = useRef(`csEngine_${Date.now()}`);

  const [study,        setStudy]        = useState(null);
  const [imageIds,     setImageIds]     = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [activeTool,   setActiveTool]   = useState("WindowLevel");
  const [windowPreset, setWindowPreset] = useState("Auto");
  const [wc,           setWc]           = useState(null);
  const [ww,           setWw]           = useState(null);
  const [zoom,         setZoom]         = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reportOpen,   setReportOpen]   = useState(false);
  const [csReady,      setCsReady]      = useState(false);

  // ── Load study metadata ──────────────────────────────────────────────────
  useEffect(() => {
    if (!studyId) return;
    (async () => {
      const { data, error: err } = await db
        .from("dicom_studies")
        .select("*")
        .eq("id", studyId)
        .maybeSingle();
      if (err || !data) { setError("Study not found."); setLoading(false); return; }
      setStudy(data);
    })();
  }, [studyId]); // eslint-disable-line

  // ── Generate signed URLs and build imageId list ──────────────────────────
  useEffect(() => {
    if (!study) return;
    (async () => {
      try {
        const files = Array.isArray(study.file_names) ? study.file_names : [];
        if (files.length === 0) {
          setError("No DICOM files attached to this study.");
          setLoading(false);
          return;
        }

        const ids = [];
        for (const filename of files) {
          const filePath = `${study.storage_path}/${filename}`;
          const signedUrl = await storage.getSignedUrl("dicom-files", filePath, 3600).catch(() => "");
          if (signedUrl) {
            ids.push(`wadouri:${signedUrl}`);
          }
        }
        setImageIds(ids);
      } catch (e) {
        setError("Failed to load DICOM files: " + e.message);
        setLoading(false);
      }
    })();
  }, [study]); // eslint-disable-line

  // ── Initialize Cornerstone viewport ─────────────────────────────────────
  useEffect(() => {
    if (imageIds.length === 0 || !viewportRef.current) return;

    let engine = null;
    let cancelled = false;

    (async () => {
      try {
        await initCornerstone();

        // Load @cornerstonejs/core and @cornerstonejs/tools from npm (no extern issues).
        // dicom-image-loader is loaded via CDN script tag in index.html as a global
        // to bypass CRA's bundling order problem with .external assignment.
        const csCore  = await import("@cornerstonejs/core");
        const csTools = await import("@cornerstonejs/tools");

        // Access DICOM image loader from CDN globals set by index.html scripts.
        const loader = window.cornerstoneDICOMImageLoader;
        const parser = window.dicomParser;

        if (loader && parser) {
          loader.external.cornerstone = csCore;
          loader.external.dicomParser = parser;
          loader.configure({ useWebWorkers: false });
        } else {
          console.error("[DICOMViewer] CDN scripts not loaded — check index.html script tags");
          setError("DICOM loader not available. Please reload the page.");
          setLoading(false);
          return;
        }

        if (cancelled) return;

        // Destroy any previous engine for this id
        try { csCore.getRenderingEngine(renderEngineId.current)?.destroy(); } catch {}

        engine = new csCore.RenderingEngine(renderEngineId.current);
        engineRef.current = engine;

        const viewportInput = {
          viewportId: viewportIdRef.current,
          type: csCore.Enums.ViewportType.STACK,
          element: viewportRef.current,
        };
        engine.enableElement(viewportInput);

        const viewport = engine.getViewport(viewportIdRef.current);
        await viewport.setStack(imageIds, 0);
        viewport.render();

        // Register and activate tools
        const {
          WindowLevelTool, ZoomTool, PanTool, LengthTool,
          AngleTool, MagnifyTool, ToolGroupManager, Enums: ToolEnums,
        } = csTools;

        const toolGroupId = `tg_${viewportIdRef.current}`;
        try { ToolGroupManager.destroyToolGroup(toolGroupId); } catch {}
        const toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

        [WindowLevelTool, ZoomTool, PanTool, LengthTool, AngleTool, MagnifyTool].forEach(T => {
          try { csTools.addTool(T); } catch {}
          toolGroup.addTool(T.toolName);
        });
        toolGroup.addViewport(viewportIdRef.current, renderEngineId.current);
        toolGroup.setToolActive(WindowLevelTool.toolName, {
          bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
        });
        toolGroup.setToolActive(PanTool.toolName, {
          bindings: [{ mouseButton: ToolEnums.MouseBindings.Auxiliary }],
        });
        toolGroup.setToolActive(ZoomTool.toolName, {
          bindings: [{ mouseButton: ToolEnums.MouseBindings.Secondary }],
        });
        // Annotation tools must be explicitly set to Passive during init.
        // addTool() leaves them in Disabled state; switchTool() needs them
        // in Passive so setToolActive() transitions correctly.
        toolGroup.setToolPassive(LengthTool.toolName);
        toolGroup.setToolPassive(AngleTool.toolName);
        toolGroup.setToolPassive(MagnifyTool.toolName);

        if (!cancelled) { setCsReady(true); setLoading(false); }
      } catch (e) {
        if (!cancelled) {
          console.error("[DICOMViewer] init error:", e);
          setError("DICOM viewer failed to initialize. " + e.message);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { engineRef.current?.destroy(); } catch {}
      engineRef.current = null;
    };
  }, [imageIds]); // eslint-disable-line

  // ── Tool switching ───────────────────────────────────────────────────────
  const switchTool = useCallback(async (toolName) => {
    if (!csReady) return;
    try {
      const csTools = await import("@cornerstonejs/tools");
      const { ToolGroupManager, Enums: ToolEnums } = csTools;
      const toolGroupId = `tg_${viewportIdRef.current}`;
      const tg = ToolGroupManager.getToolGroup(toolGroupId);
      if (!tg) return;
      const allTools = [
        csTools.WindowLevelTool.toolName, csTools.ZoomTool.toolName,
        csTools.PanTool.toolName, csTools.LengthTool.toolName,
        csTools.AngleTool.toolName, csTools.MagnifyTool.toolName,
      ];
      allTools.forEach(t => {
        if (t !== csTools.PanTool.toolName && t !== csTools.ZoomTool.toolName) {
          try { tg.setToolPassive(t); } catch {}
        }
      });
      tg.setToolActive(toolName, {
        bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
      });
      setActiveTool(toolName);
    } catch (e) { console.error("[DICOMViewer] switchTool:", e); }
  }, [csReady]);

  // ── Navigate images ──────────────────────────────────────────────────────
  const goToImage = useCallback(async (index) => {
    if (!csReady || !engineRef.current) return;
    const idx = Math.max(0, Math.min(index, imageIds.length - 1));
    try {
      const csCore = await import("@cornerstonejs/core");
      const viewport = csCore.getRenderingEngine(renderEngineId.current)
        ?.getViewport(viewportIdRef.current);
      if (!viewport) return;
      await viewport.setImageIdIndex(idx);
      viewport.render();
      setCurrentIndex(idx);
    } catch (e) { console.error("[DICOMViewer] goToImage:", e); }
  }, [csReady, imageIds.length]); // eslint-disable-line

  // ── Apply window preset ──────────────────────────────────────────────────
  const applyPreset = useCallback(async (preset) => {
    setWindowPreset(preset.label);
    if (!csReady || !engineRef.current) return;
    if (preset.wc === null) return; // Auto — leave as is
    try {
      const csCore = await import("@cornerstonejs/core");
      const viewport = csCore.getRenderingEngine(renderEngineId.current)
        ?.getViewport(viewportIdRef.current);
      if (!viewport) return;
      viewport.setProperties({ voiRange: { lower: preset.wc - preset.ww / 2, upper: preset.wc + preset.ww / 2 } });
      viewport.render();
      setWc(preset.wc);
      setWw(preset.ww);
    } catch (e) { console.error("[DICOMViewer] applyPreset:", e); }
  }, [csReady]);

  // ── Invert ───────────────────────────────────────────────────────────────
  const toggleInvert = useCallback(async () => {
    if (!csReady || !engineRef.current) return;
    try {
      const csCore = await import("@cornerstonejs/core");
      const viewport = csCore.getRenderingEngine(renderEngineId.current)
        ?.getViewport(viewportIdRef.current);
      if (!viewport) return;
      const { invert } = viewport.getProperties();
      viewport.setProperties({ invert: !invert });
      viewport.render();
    } catch (e) { console.error("[DICOMViewer] invert:", e); }
  }, [csReady]);

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = useCallback(async () => {
    if (!csReady || !engineRef.current) return;
    try {
      const csCore = await import("@cornerstonejs/core");
      const viewport = csCore.getRenderingEngine(renderEngineId.current)
        ?.getViewport(viewportIdRef.current);
      if (!viewport) return;
      viewport.resetCamera();
      viewport.resetProperties();
      viewport.render();
      setWindowPreset("Auto");
      setWc(null); setWw(null); setZoom(1.0);
    } catch (e) { console.error("[DICOMViewer] reset:", e); }
  }, [csReady]);

  // ── Download current frame ───────────────────────────────────────────────
  const handleDownload = useCallback(() => {
    if (!viewportRef.current) return;
    const canvas = viewportRef.current.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `dicom_frame_${currentIndex + 1}.png`;
    a.click();
  }, [currentIndex]);

  const mColor = modalityColor(study?.modality);

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: C.navy, display: "flex", flexDirection: "column",
    }}>
      {/* ── Top toolbar ──────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, height: 46, background: C.toolbar,
        borderBottom: `1px solid ${C.border2}`,
        display: "flex", alignItems: "center", gap: 6, padding: "0 10px",
        overflowX: "auto",
      }}>
        {/* Study info */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 10, flexShrink: 0 }}>
          <div style={{ width: 28, height: 28, borderRadius: 5, background: `${mColor}22`, border: `1px solid ${mColor}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: mColor }}>
            {study?.modality || "—"}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text1, lineHeight: 1.2 }}>
              {study?.description || study?.body_part || "DICOM Study"}
            </div>
            <div style={{ fontSize: 10, color: C.text3 }}>
              {study?.study_date || "—"}
            </div>
          </div>
        </div>

        <div style={{ width: 1, height: 28, background: C.border2, flexShrink: 0 }} />

        {/* Drawing tools */}
        {[
          { key: "WindowLevel",  label: "W/L",     title: "Window / Level" },
          { key: "Zoom",         label: "Zoom",    title: "Zoom"           },
          { key: "Pan",          label: "Pan",     title: "Pan"            },
          { key: "Length",       label: "Length",  title: "Measure Length" },
          { key: "Angle",        label: "Angle",   title: "Measure Angle"  },
          { key: "Magnify",      label: "Magnify", title: "Magnify"        },
        ].map(t => (
          <TBtn key={t.key} label={t.label} title={t.title}
            active={activeTool === t.key}
            onClick={() => switchTool(t.key)}
            disabled={!csReady}
          />
        ))}

        <div style={{ width: 1, height: 28, background: C.border2, flexShrink: 0 }} />

        {/* Window presets */}
        <select
          value={windowPreset}
          onChange={e => {
            const p = WINDOW_PRESETS.find(x => x.label === e.target.value);
            if (p) applyPreset(p);
          }}
          disabled={!csReady}
          style={{ background: C.navy4, border: `1px solid ${C.border2}`, borderRadius: 5, color: C.text1, fontSize: 11, padding: "3px 6px", cursor: "pointer" }}
        >
          {WINDOW_PRESETS.map(p => <option key={p.label} value={p.label}>{p.label}</option>)}
        </select>

        <div style={{ width: 1, height: 28, background: C.border2, flexShrink: 0 }} />

        <TBtn label="Invert" onClick={toggleInvert} disabled={!csReady} />
        <TBtn label="Reset"  onClick={handleReset}  disabled={!csReady} />

        <div style={{ width: 1, height: 28, background: C.border2, flexShrink: 0 }} />

        {/* Navigation */}
        <TBtn label="◀" title="Previous Image" disabled={!csReady || currentIndex === 0}
          onClick={() => goToImage(currentIndex - 1)} />
        <div style={{ fontSize: 11, color: C.text2, minWidth: 60, textAlign: "center", flexShrink: 0 }}>
          {imageIds.length > 0 ? `${currentIndex + 1} / ${imageIds.length}` : "— / —"}
        </div>
        <TBtn label="▶" title="Next Image" disabled={!csReady || currentIndex >= imageIds.length - 1}
          onClick={() => goToImage(currentIndex + 1)} />

        <div style={{ flex: 1 }} />

        {/* Actions */}
        {study?.report_text && (
          <TBtn label="Report" onClick={() => setReportOpen(r => !r)} active={reportOpen} />
        )}
        <TBtn label="⬇ PNG" title="Download current frame as PNG"
          onClick={handleDownload} disabled={!csReady} />
        <TBtn label={isFullscreen ? "⤢ Exit" : "⤡ Full"} title="Toggle fullscreen"
          onClick={() => {
            setIsFullscreen(f => !f);
            if (!document.fullscreenElement) {
              document.documentElement.requestFullscreen?.().catch(() => {});
            } else {
              document.exitFullscreen?.().catch(() => {});
            }
          }}
        />

        <div style={{ width: 1, height: 28, background: C.border2, flexShrink: 0 }} />
        <button onClick={onClose} style={{ background: "none", border: "none", color: C.text2, fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1 }} title="Close viewer">✕</button>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>

        {/* Loading overlay */}
        {loading && (
          <div style={{ position: "absolute", inset: 0, zIndex: 10, background: C.navy, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${C.border2}`, borderTop: `3px solid ${C.teal}`, animation: "spin 1s linear infinite" }} />
            <div style={{ fontSize: 13, color: C.text3 }}>Loading DICOM images…</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div style={{ position: "absolute", inset: 0, zIndex: 10, background: C.navy, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <div style={{ fontSize: 32 }}>⚠</div>
            <div style={{ fontSize: 14, color: "#f87171", maxWidth: 400, textAlign: "center" }}>{error}</div>
            <button onClick={onClose} style={{ marginTop: 8, padding: "8px 20px", borderRadius: 6, background: C.navy3, border: `1px solid ${C.border2}`, color: C.text1, cursor: "pointer", fontSize: 13 }}>Close</button>
          </div>
        )}

        {/* Viewport canvas */}
        <div
          ref={viewportRef}
          style={{ flex: 1, background: "#000", position: "relative" }}
        />

        {/* Report side panel */}
        {reportOpen && study?.report_text && (
          <div style={{
            width: 380, flexShrink: 0, background: C.navy2, borderLeft: `1px solid ${C.border2}`,
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text1 }}>Radiology Report</div>
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                background: study.report_status === "final" ? "rgba(34,197,94,0.12)" : "var(--amber-lt,rgba(240,165,0,0.12))",
                color: study.report_status === "final" ? "#22c55e" : "#f0a500",
              }}>
                {(study.report_status || "pending").toUpperCase()}
              </span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
              {study.reading_radiologist && (
                <div style={{ fontSize: 11, color: C.text3, marginBottom: 10 }}>
                  Read by: {study.reading_radiologist}
                </div>
              )}
              <pre style={{ fontFamily: "var(--mono,monospace)", fontSize: 11, lineHeight: 1.8, color: C.text2, whiteSpace: "pre-wrap", margin: 0 }}>
                {study.report_text}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* ── Bottom status bar ─────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, height: 28, background: C.toolbar,
        borderTop: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 16, padding: "0 14px",
        fontSize: 10, color: C.text3,
      }}>
        <span>Image: {imageIds.length > 0 ? `${currentIndex + 1} / ${imageIds.length}` : "—"}</span>
        {wc !== null && <span>WC: {wc} / WW: {ww}</span>}
        <span>Zoom: {zoom.toFixed(2)}×</span>
        {study?.study_date && <span>Study: {study.study_date}</span>}
        {study?.modality && <span style={{ color: mColor, fontWeight: 600 }}>{study.modality}</span>}
        {study?.image_count > 0 && <span>{study.image_count} image{study.image_count !== 1 ? "s" : ""}</span>}
      </div>
    </div>,
    document.body
  );
}
